import React, { useState, useEffect, useContext, useMemo } from "react";
import { motion as m, AnimatePresence } from "framer-motion";

import Bookmark from "../components/Bookmark";
import ProjectCard from "../components/ProjectCard";
import Button from "../components/Button";

import { CursorContext } from "../context/CursorContext";
import { animationVariants } from "../config/animationConstants";

import data from "../data/data";

const Projects = () => {
  const [projectTitle, setProjectTitle] = useState("");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animateProjects, setAnimateProjects] = useState(false);
  const [projectsPerSlide, setProjectsPerSlide] = useState(6);
  const [isMobile, setIsMobile] = useState(false);

  const { mouseEnterHandler, mouseLeaverHandler } = useContext(CursorContext);

  useEffect(() => {
    setAnimateProjects(true);

    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setProjectsPerSlide(1);
        setIsMobile(true);
      } else {
        setProjectsPerSlide(6);
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const slides = useMemo(() => {
    const tempSlides = [];
    for (let i = 0; i < data.length; i += projectsPerSlide) {
      tempSlides.push(data.slice(i, i + projectsPerSlide));
    }
    return tempSlides;
  }, [projectsPerSlide]);

  useEffect(() => {
    if (isMobile && slides[currentSlide]) {
      setProjectTitle(slides[currentSlide][0].title);
    } else if (!isMobile) {
      setProjectTitle("");
    }
  }, [currentSlide, isMobile, slides]);

  return (
    <section className='section'>
      <Bookmark />
      <div className='col-span-10 flex flex-col lg:flex-row items-center p-4 lg:p-0'>
        <m.div
          initial='hidden'
          animate='visible'
          variants={animationVariants}
          onMouseEnter={mouseEnterHandler}
          onMouseLeave={mouseLeaverHandler}
          className='w-full p-2 md:p-4 mx-auto'
        >
          <div className='flex flex-col items-center gap-6 w-full'>
            <p className='block h-6 text-xl text-secondary font-bold relative z-10'>
              {projectTitle}
            </p>
            <div className='w-full lg:min-h-[520px]'>
              <AnimatePresence mode='wait'>
                <m.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.4 }}
                  className='grid grid-cols-1 lg:grid-cols-3 gap-4 w-full items-start'
                >
                  {slides[currentSlide].map((project, index) => (
                    <m.div
                      key={project.id}
                      initial='hidden'
                      animate={animateProjects ? "visible" : "hidden"}
                      variants={{
                        hidden: { opacity: 0, y: 16 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            delay: 0.08 * index,
                          },
                        },
                      }}
                      onMouseEnter={() =>
                        !isMobile && setProjectTitle(project.title)
                      }
                      onMouseLeave={() => !isMobile && setProjectTitle("")}
                    >
                      <ProjectCard project={project} />
                    </m.div>
                  ))}
                </m.div>
              </AnimatePresence>
            </div>
            <div className='lg:hidden flex gap-4 mt-1 relative z-10'>
              <Button
                buttonConfig={{
                  id: "prevSlide",
                  text: "Previous",
                  onClick: () =>
                    setCurrentSlide((prev) =>
                      prev === 0 ? slides.length - 1 : prev - 1,
                    ),
                  disabled: slides.length <= 1,
                }}
              />
              <Button
                buttonConfig={{
                  id: "nextSlide",
                  text: "Next",
                  onClick: () =>
                    setCurrentSlide((prev) =>
                      prev === slides.length - 1 ? 0 : prev + 1,
                    ),
                  disabled: slides.length <= 1,
                }}
              />
            </div>
          </div>
        </m.div>
      </div>
    </section>
  );
};

export default Projects;
